import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  Input,
  output,
  inject,
} from '@angular/core';

import type { User } from '@core/account-types';
import { AdminsAvatarsComponent, AvatarUser } from '@core/account-ui';
import { AddEvaluationDropdownComponent } from '@core/add-evaluations-dropdown-legacy';
import type { QuickEvaluationContext } from '@core/analytics-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { InterviewGroupBy } from '@core/interview-results-types';
import { AppNavigatorService } from '@core/navigation-legacy';
import type { TOfferKind } from '@core/offers-types';
import { OfferSelectOption, OfferStatusIndicatorComponent } from '@core/offers-ui';
import type { Stage } from '@core/stages-types';
import type { SelectGroupedOptions } from '@core/user-interface-legacy';
import {
  BoxComponent,
  ButtonComponent,
  ConnectedOverlayDirective,
  GroupComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  RtTooltip,
  TruncateComponent,
} from '@core/user-interface-legacy';

import { injectUnbiasedHiringTranslations } from '../../translations';

@Component({
  selector: 'rt-hidden-evaluations-infobox',
  templateUrl: './hidden-evaluations-infobox.component.html',
  styleUrls: ['./hidden-evaluations-infobox.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BoxComponent,
    IconComponent,
    OfferStatusIndicatorComponent,
    TruncateComponent,
    AdminsAvatarsComponent,
    ButtonComponent,
    GroupComponent,
    OverlayOriginDirective,
    RtTooltip,
    IconButtonComponent,
    ConnectedOverlayDirective,
    AddEvaluationDropdownComponent,
    TranslatePipe,
  ],
})
export class HiddenEvaluationsInfoboxComponent {
  private readonly appNavigator = inject(AppNavigatorService);

  public readonly translations = injectUnbiasedHiringTranslations();

  public readonly admins = input([], {
    transform: (value: AvatarUser[] | undefined) => value || [],
  });
  public readonly guests = input([], {
    transform: (value: AvatarUser[] | undefined) => value || [],
  });
  public readonly stage = input<Stage>();
  public readonly isJobNameVisible = input<boolean>(undefined!);
  public readonly isButtonsVisible = input<boolean>(undefined!);
  public readonly groupBy = input<InterviewGroupBy>();
  public readonly count = input<number>(undefined!);
  public readonly short = input<boolean | undefined>(false);
  public readonly offers = input<OfferSelectOption[]>();
  public readonly templates = input<SelectGroupedOptions[]>();
  public readonly candidateId = input<number>();
  public readonly marginBottom = input<boolean>();
  public readonly canReveal = input<boolean>(false);
  public readonly hasAccessToJob = input<boolean>();

  @Input() public admin?: User;
  @Input() public offer: { title: string; status: string; kind: TOfferKind; id: number } | null;

  public members = computed(() => [...this.admins(), ...this.guests()]);
  public quickEvaluationClick = output<{
    offerId: number;
    context: QuickEvaluationContext;
  }>();
  public counterClick = output<void>();
  public revealClick = output<void>();

  public renderAdminTooltip(adminOrGuest: AvatarUser) {
    return adminOrGuest.name || `${adminOrGuest.firstName} ${adminOrGuest.lastName}`;
  }

  public onSubmit(event: {
    type: string;
    value: { talentPoolId?: number; offerId?: number; templateId: number; admins: User[] };
  }) {
    this.appNavigator.openCandidateDialog(this.candidateId()!, {
      openInterview: true,
      interviewOfferId: event.value.offerId,
      interviewTalentPoolId: event.value.talentPoolId,
      interviewTemplateId: event.value.templateId,
      fromHiddenEvaluations: true,
    });
  }

  public get correctOffer() {
    return <OfferSelectOption & { id: number }>(
      this.offers()?.find(offer => this.offer?.id === offer.id)!
    );
  }
}
